import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, Link } from "react-router-dom";
import { User, Heart, MessageSquare, Bell, Building2, PlusCircle, LogOut, Clock, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import PropertyCard from "@/components/site/PropertyCard";

function Sidebar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const items = [
    { to: "profile", icon: User, label: t("dashboard.myProfile") },
    { to: "properties", icon: Building2, label: t("dashboard.myProps") },
    { to: "favorites", icon: Heart, label: t("dashboard.saved") },
    { to: "enquiries", icon: MessageSquare, label: t("dashboard.enquiries") },
    { to: "notifications", icon: Bell, label: t("dashboard.notifs") },
  ];
  return (
    <aside className="bg-white border border-slate-200 rounded-2xl p-4 lg:sticky lg:top-24 self-start">
      <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
        <div className="w-11 h-11 rounded-full bg-kp-teal text-white flex items-center justify-center font-semibold">{user?.name?.[0]?.toUpperCase() || "U"}</div>
        <div>
          <div className="font-semibold text-slate-900">{user?.name}</div>
          <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
        </div>
      </div>
      <nav className="mt-3 space-y-1">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-kp-teal-light text-kp-teal font-semibold" : "text-slate-700 hover:bg-slate-50"}`} data-testid={`dash-nav-${it.to}`}>
            <it.icon size={16}/> {it.label}
          </NavLink>
        ))}
        <Link to="/post-property" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-kp-maroon font-semibold hover:bg-kp-maroon-light"><PlusCircle size={16}/> {t("dashboard.postProp")}</Link>
        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50"><LogOut size={16}/> {t("dashboard.logout")}</button>
      </nav>
    </aside>
  );
}

const Profile = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <h2 className="font-display text-xl font-semibold text-slate-900">{t("dashboard.myProfile")}</h2>
      <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
        <div><div className="text-slate-500 text-xs">{t("dashboard.name")}</div><div className="font-medium">{user?.name}</div></div>
        <div><div className="text-slate-500 text-xs">{t("dashboard.phone")}</div><div className="font-medium">+91 {user?.phone}</div></div>
        <div><div className="text-slate-500 text-xs">{t("dashboard.email")}</div><div className="font-medium">{user?.email || "—"}</div></div>
        <div><div className="text-slate-500 text-xs">{t("dashboard.role")}</div><div className="font-medium capitalize">{user?.role}</div></div>
      </div>
    </div>
  );
};

const PropertyStatusCard = ({ p, t }) => {
  const isPending = p.status === "pending" || !p.verified;
  return (
    <div className="relative">
      {isPending && (
        <div className="absolute z-10 top-3 left-3 right-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-sm">
          <Clock size={13}/> <span className="font-semibold">{t("common.pending")}</span>
          <span className="text-amber-700/80 ml-1 hidden sm:inline">— {t("dashboard.pendingBanner")}</span>
        </div>
      )}
      <PropertyCard property={p}/>
    </div>
  );
};

const MyProperties = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  useEffect(() => { api.get("/my/properties").then(r => setItems(r.data)).catch(() => {}); }, []);
  const filtered = tab === "all" ? items : items.filter(p => p.purpose === tab);
  const counts = { all: items.length, sale: items.filter(p => p.purpose === "sale").length, rent: items.filter(p => p.purpose === "rent").length, lease: items.filter(p => p.purpose === "lease").length };
  const tabs = [["all", t("dashboard.all")], ["sale", t("dashboard.sale")], ["rent", t("dashboard.rent")], ["lease", t("dashboard.lease")]];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display text-xl font-semibold text-slate-900">{t("dashboard.myProps")}</h2>
        <Link to="/post-property" className="btn-maroon rounded-full text-sm px-4 py-2 font-semibold flex items-center gap-1"><PlusCircle size={14}/> {t("dashboard.postProp")}</Link>
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto no-scrollbar">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === k ? "border-kp-teal text-kp-teal" : "border-transparent text-slate-600 hover:text-kp-teal"}`} data-testid={`myprops-tab-${k}`}>
            {l} <span className="ml-1 text-xs bg-slate-100 rounded-full px-1.5">{counts[k]}</span>
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">
          {t("dashboard.none")} <Link to="/post-property" className="text-kp-teal font-semibold ml-1">{t("dashboard.postOne")}</Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">{filtered.map((p) => <PropertyStatusCard key={p.id} p={p} t={t}/>)}</div>
      )}
    </div>
  );
};

const Favorites = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/favorites").then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">{t("dashboard.saved")}</h2>
      {items.length === 0 ? <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">{t("dashboard.none")}</div>
        : <div className="grid gap-4 sm:grid-cols-2">{items.map((p) => <PropertyCard key={p.id} property={p}/>)}</div>}
    </div>
  );
};

const Enquiries = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/my/enquiries").then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border rounded-2xl p-5">
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">{t("dashboard.enquiries")}</h2>
      {items.length === 0 ? <p className="text-slate-500">{t("dashboard.none")}</p>
        : <div className="space-y-3">{items.map((e) => (
          <div key={e.id} className="border rounded-lg p-3">
            <div className="text-sm font-semibold">{e.subject || "Enquiry"}</div>
            <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</div>
            <div className="text-sm mt-1">{e.message}</div>
          </div>
        ))}</div>}
    </div>
  );
};

const Notifications = () => {
  const { t } = useI18n();
  return <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">{t("dashboard.notifs")} ✓</div>;
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/" replace/>;
  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8 grid lg:grid-cols-[260px,1fr] gap-6">
      <Sidebar/>
      <main>
        <Routes>
          <Route index element={<Navigate to="profile" replace/>}/>
          <Route path="profile" element={<Profile/>}/>
          <Route path="properties" element={<MyProperties/>}/>
          <Route path="favorites" element={<Favorites/>}/>
          <Route path="enquiries" element={<Enquiries/>}/>
          <Route path="notifications" element={<Notifications/>}/>
        </Routes>
      </main>
    </div>
  );
}
